import React, { useState, useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { supabase } from '../lib/supabase'

const teal = '#0D9488'
const plum = '#1E1040'

const CARD_TYPES = [
  { value: 'credit', icon: '💳', requiresLast4: true },
  { value: 'debit', icon: '💳', requiresLast4: true },
  { value: 'cash', icon: '💵', requiresLast4: false },
  { value: 'check', icon: '📝', requiresLast4: false },
  { value: 'other', icon: '🔗', requiresLast4: false },
]

const cardTypeMeta = (type) => CARD_TYPES.find(c => c.value === type) || CARD_TYPES[0]

const TAG_COLORS = {
  schedule_c: teal,
  company_card: '#2563EB',
  reimbursable: '#F59E0B',
  personal: '#A855F7',
}

/**
 * ManagePaymentMethods
 * Settings section for adding/editing/deleting payment methods.
 * Each payment method has an optional default tag, which auto-selects
 * when the user picks that method during receipt upload.
 */
export default function ManagePaymentMethods() {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [paymentMethods, setPaymentMethods] = useState([])
  const [tags, setTags] = useState([])
  const [receiptCounts, setReceiptCounts] = useState({}) // { pm_id: count }

  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState(null)
  const [deleteError, setDeleteError] = useState('')

  // Form state (shared between add + edit modes)
  const [formLabel, setFormLabel] = useState('')
  const [formCardType, setFormCardType] = useState('credit')
  const [formLast4, setFormLast4] = useState('')
  const [formDefaultTagId, setFormDefaultTagId] = useState('')
  const [formError, setFormError] = useState('')

  useEffect(() => { loadAll() }, [])

  const loadAll = async () => {
    setLoading(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoading(false); return }

    const [
      { data: pmData },
      { data: tagsData },
      { data: txData },
    ] = await Promise.all([
      supabase.from('payment_methods').select('*').eq('user_id', user.id).order('sort_order'),
      supabase.from('tags').select('*').eq('user_id', user.id).order('sort_order'),
      supabase.from('transactions').select('payment_method_id').eq('user_id', user.id),
    ])

    const counts = {}
    ;(txData || []).forEach(tt => {
      if (tt.payment_method_id) counts[tt.payment_method_id] = (counts[tt.payment_method_id] || 0) + 1
    })

    setPaymentMethods(pmData || [])
    setTags(tagsData || [])
    setReceiptCounts(counts)
    setLoading(false)
  }

  const resetForm = () => {
    setFormLabel('')
    setFormCardType('credit')
    setFormLast4('')
    setFormDefaultTagId('')
    setFormError('')
  }

  const startAdd = () => {
    resetForm()
    setAdding(true)
    setEditingId(null)
    setDeleteConfirmId(null)
  }

  const cancelAdd = () => {
    setAdding(false)
    resetForm()
  }

  const startEdit = (pm) => {
    setFormLabel(pm.label)
    setFormCardType(pm.card_type)
    setFormLast4(pm.last_4 || '')
    setFormDefaultTagId(pm.default_tag_id || '')
    setFormError('')
    setEditingId(pm.id)
    setAdding(false)
    setDeleteConfirmId(null)
  }

  const cancelEdit = () => {
    setEditingId(null)
    resetForm()
  }

  const validate = () => {
    const trimmed = formLabel.trim()
    if (!trimmed) return t('manage_payment.error_label_required')
    if (trimmed.length > 40) return t('manage_payment.error_label_too_long')

    const meta = cardTypeMeta(formCardType)
    if (meta.requiresLast4) {
      const last4Trim = (formLast4 || '').trim()
      if (!last4Trim) return t('manage_payment.error_last4_required')
      if (!/^\d{4}$/.test(last4Trim)) return t('manage_payment.error_last4_format')
    }
    return null
  }

  const saveAdd = async () => {
    const err = validate()
    if (err) { setFormError(err); return }

    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return

    const meta = cardTypeMeta(formCardType)
    const payload = {
      user_id: user.id,
      label: formLabel.trim(),
      card_type: formCardType,
      last_4: meta.requiresLast4 ? formLast4.trim() : null,
      default_tag_id: formDefaultTagId || null,
      is_default: paymentMethods.length === 0, // first one added = default
      sort_order: paymentMethods.length + 1,
    }

    const { data: newPm, error: insertErr } = await supabase
      .from('payment_methods')
      .insert(payload)
      .select()
      .single()

    if (insertErr) { setFormError(insertErr.message); return }

    setPaymentMethods(prev => [...prev, newPm])
    cancelAdd()
  }

  const saveEdit = async () => {
    const err = validate()
    if (err) { setFormError(err); return }

    const meta = cardTypeMeta(formCardType)
    const payload = {
      label: formLabel.trim(),
      card_type: formCardType,
      last_4: meta.requiresLast4 ? formLast4.trim() : null,
      default_tag_id: formDefaultTagId || null,
      updated_at: new Date().toISOString(),
    }

    const { data: updated, error: updateErr } = await supabase
      .from('payment_methods')
      .update(payload)
      .eq('id', editingId)
      .select()
      .single()

    if (updateErr) { setFormError(updateErr.message); return }

    setPaymentMethods(prev => prev.map(p => p.id === updated.id ? updated : p))
    cancelEdit()
  }

  const startDelete = (pm) => {
    setDeleteConfirmId(pm.id)
    setDeleteError('')
    setAdding(false)
    setEditingId(null)
  }

  const cancelDelete = () => {
    setDeleteConfirmId(null)
    setDeleteError('')
  }

  const confirmDelete = async () => {
    const pm = paymentMethods.find(p => p.id === deleteConfirmId)
    if (!pm) return

    const count = receiptCounts[pm.id] || 0
    if (count > 0) {
      setDeleteError(t('manage_payment.error_in_use', { count }))
      return
    }

    const { error: err } = await supabase.from('payment_methods').delete().eq('id', pm.id)
    if (err) { setDeleteError(err.message); return }

    setPaymentMethods(prev => prev.filter(p => p.id !== pm.id))
    setDeleteConfirmId(null)
    setDeleteError('')
  }

  const getTag = (tagId) => tags.find(tt => tt.id === tagId)

  if (loading) {
    return <div style={styles.loading}>{t('manage_payment.loading')}</div>
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>{t('manage_payment.title')}</h2>
          <p style={styles.subtitle}>
            {t('manage_payment.subtitle')}
          </p>
        </div>
        {!adding && editingId === null && (
          <button style={styles.addBtn} onClick={startAdd}>
            + {t('manage_payment.add_btn')}
          </button>
        )}
      </div>

      {/* Add form */}
      {adding && (
        <div style={styles.formBox}>
          <div style={styles.editLabel}>{t('manage_payment.add_label')}</div>
          <FormFields
            t={t}
            formLabel={formLabel} setFormLabel={setFormLabel}
            formCardType={formCardType} setFormCardType={setFormCardType}
            formLast4={formLast4} setFormLast4={setFormLast4}
            formDefaultTagId={formDefaultTagId} setFormDefaultTagId={setFormDefaultTagId}
            tags={tags}
          />
          {formError && <div style={styles.error}>{formError}</div>}
          <div style={styles.editActions}>
            <button style={styles.btnSecondary} onClick={cancelAdd}>{t('common.cancel')}</button>
            <button style={styles.btnPrimary} onClick={saveAdd}>
              {t('manage_payment.add_short')}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {paymentMethods.length === 0 && !adding ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>💳</div>
          <div style={styles.emptyTitle}>{t('manage_payment.empty_title')}</div>
          <div style={styles.emptySub}>
            {t('manage_payment.empty_sub')}
          </div>
          <button style={styles.emptyBtn} onClick={startAdd}>
            + {t('manage_payment.empty_btn')}
          </button>
        </div>
      ) : (
        <div style={styles.list}>
          {paymentMethods.map(pm => {
            const meta = cardTypeMeta(pm.card_type)
            const defaultTag = getTag(pm.default_tag_id)
            const tagAccent = defaultTag ? TAG_COLORS[defaultTag.tag_type] : '#6B7280'
            const count = receiptCounts[pm.id] || 0

            return (
              <div key={pm.id} style={styles.row}>
                {editingId === pm.id ? (
                  <div style={styles.editBlock}>
                    <div style={styles.editLabel}>{t('manage_payment.edit_label')}</div>
                    <FormFields
                      t={t}
                      formLabel={formLabel} setFormLabel={setFormLabel}
                      formCardType={formCardType} setFormCardType={setFormCardType}
                      formLast4={formLast4} setFormLast4={setFormLast4}
                      formDefaultTagId={formDefaultTagId} setFormDefaultTagId={setFormDefaultTagId}
                      tags={tags}
                    />
                    {formError && <div style={styles.error}>{formError}</div>}
                    <div style={styles.editActions}>
                      <button style={styles.btnSecondary} onClick={cancelEdit}>{t('common.cancel')}</button>
                      <button style={styles.btnPrimary} onClick={saveEdit}>{t('manage_payment.save_changes')}</button>
                    </div>
                  </div>
                ) : deleteConfirmId === pm.id ? (
                  <div style={styles.deleteBlock}>
                    <div style={styles.deleteText}>
                      <Trans
                        i18nKey="manage_payment.delete_confirm"
                        values={{ label: pm.label, last4: pm.last_4 ? ` ••${pm.last_4}` : '' }}
                        components={{ strong: <strong /> }}
                      />
                    </div>
                    {deleteError && <div style={styles.error}>{deleteError}</div>}
                    <div style={styles.editActions}>
                      <button style={styles.btnSecondary} onClick={cancelDelete}>{t('common.cancel')}</button>
                      <button style={styles.btnDanger} onClick={confirmDelete}>{t('manage_payment.confirm_delete')}</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={styles.rowMain}>
                      <span style={styles.rowIcon}>{meta.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={styles.rowName}>
                          {pm.label}
                          {pm.last_4 && <span style={styles.last4}>••{pm.last_4}</span>}
                        </div>
                        <div style={styles.rowMeta}>
                          <span>{t(`manage_payment.types.${pm.card_type}`)}</span>
                          {defaultTag && (
                            <span style={{
                              ...styles.tagPill,
                              color: tagAccent,
                              background: `${tagAccent}1A`,
                            }}>
                              → {defaultTag.icon} {defaultTag.name}
                            </span>
                          )}
                          <span style={styles.rowCount}>
                            {t('manage_payment.receipt_count', { count })}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div style={styles.rowActions}>
                      <button style={styles.actionBtn} onClick={() => startEdit(pm)}>{t('common.edit')}</button>
                      <button style={styles.actionBtnDanger} onClick={() => startDelete(pm)}>{t('common.delete')}</button>
                    </div>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ---------- Form fields (shared between add + edit) ----------
function FormFields({
  t,
  formLabel, setFormLabel,
  formCardType, setFormCardType,
  formLast4, setFormLast4,
  formDefaultTagId, setFormDefaultTagId,
  tags,
}) {
  const meta = cardTypeMeta(formCardType)
  return (
    <>
      <div style={styles.field}>
        <label style={styles.fieldLabel}>{t('manage_payment.label_field')} *</label>
        <input
          type="text"
          style={styles.input}
          placeholder={t('manage_payment.label_placeholder')}
          value={formLabel}
          onChange={e => setFormLabel(e.target.value)}
          maxLength={40}
          autoFocus
        />
      </div>

      <div style={styles.fieldRow}>
        <div style={styles.field}>
          <label style={styles.fieldLabel}>{t('manage_payment.type_field')} *</label>
          <select
            style={styles.input}
            value={formCardType}
            onChange={e => {
              setFormCardType(e.target.value)
              const newMeta = cardTypeMeta(e.target.value)
              if (!newMeta.requiresLast4) setFormLast4('')
            }}
          >
            {CARD_TYPES.map(ct => (
              <option key={ct.value} value={ct.value}>{ct.icon} {t(`manage_payment.types.${ct.value}`)}</option>
            ))}
          </select>
        </div>

        {meta.requiresLast4 && (
          <div style={styles.field}>
            <label style={styles.fieldLabel}>{t('manage_payment.last4_field')} *</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{4}"
              style={styles.input}
              placeholder="1234"
              value={formLast4}
              onChange={e => setFormLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
              maxLength={4}
            />
          </div>
        )}
      </div>

      <div style={styles.field}>
        <label style={styles.fieldLabel}>{t('manage_payment.default_tag_field')}</label>
        <select
          style={styles.input}
          value={formDefaultTagId}
          onChange={e => setFormDefaultTagId(e.target.value)}
        >
          <option value="">{t('manage_payment.default_tag_none')}</option>
          {tags.map(tag => (
            <option key={tag.id} value={tag.id}>{tag.icon} {tag.name}</option>
          ))}
        </select>
        <div style={styles.hint}>
          {t('manage_payment.default_tag_hint')}
        </div>
      </div>
    </>
  )
}

const styles = {
  container: { fontFamily: "'Plus Jakarta Sans', sans-serif", color: plum },
  loading: { padding: '32px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' },

  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    gap: '16px', marginBottom: '24px', flexWrap: 'wrap',
  },
  title: {
    fontFamily: "'Fraunces', serif", fontSize: '22px', fontWeight: 800,
    color: plum, letterSpacing: '-0.3px', margin: 0, marginBottom: '4px',
  },
  subtitle: {
    fontSize: '13px', color: '#6B7280', fontWeight: 500,
    margin: 0, lineHeight: 1.5, maxWidth: '620px',
  },

  addBtn: {
    padding: '10px 18px', background: teal, color: '#042f2e',
    border: 'none', borderRadius: '100px',
    fontSize: '13px', fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: `0 4px 14px ${teal}40`, flexShrink: 0,
  },

  empty: {
    background: '#fff', border: '1px dashed #D1D5DB',
    borderRadius: '16px', padding: '48px 32px', textAlign: 'center',
  },
  emptyIcon: { fontSize: '48px', marginBottom: '14px' },
  emptyTitle: {
    fontFamily: "'Fraunces', serif", fontSize: '18px', fontWeight: 800,
    color: plum, marginBottom: '6px', letterSpacing: '-0.2px',
  },
  emptySub: {
    fontSize: '13px', color: '#6B7280',
    marginBottom: '20px', fontWeight: 500, maxWidth: '420px',
    marginLeft: 'auto', marginRight: 'auto',
  },
  emptyBtn: {
    background: teal, color: '#042f2e', border: 'none',
    padding: '12px 24px', borderRadius: '100px',
    fontSize: '14px', fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: `0 6px 20px ${teal}40`,
  },

  list: {
    background: '#fff', border: '1px solid #E5E7EB',
    borderRadius: '14px', overflow: 'hidden',
  },
  row: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 18px', borderTop: '1px solid #F3F4F6',
    gap: '12px', flexWrap: 'wrap',
  },
  rowMain: { display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 },
  rowIcon: { fontSize: '24px' },
  rowName: {
    fontSize: '14px', fontWeight: 700, color: plum,
    display: 'flex', alignItems: 'center', gap: '8px',
  },
  last4: { fontSize: '12px', color: '#6B7280', fontWeight: 600 },
  rowMeta: {
    fontSize: '12px', color: '#9CA3AF', marginTop: '4px',
    display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
  },
  rowCount: { color: '#9CA3AF', fontWeight: 500 },
  tagPill: {
    fontSize: '11px', fontWeight: 700,
    padding: '2px 8px', borderRadius: '100px',
  },

  rowActions: { display: 'flex', gap: '8px' },
  actionBtn: {
    padding: '6px 14px', background: '#fff', color: plum,
    border: '1.5px solid #E5E7EB', borderRadius: '100px',
    fontSize: '12px', fontWeight: 600, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  actionBtnDanger: {
    padding: '6px 14px', background: '#fff', color: '#DC2626',
    border: '1.5px solid #FECACA', borderRadius: '100px',
    fontSize: '12px', fontWeight: 600, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },

  formBox: {
    background: '#F9FAFB', border: '2px solid #E5E7EB',
    borderRadius: '12px', padding: '18px', marginBottom: '14px',
  },
  editBlock: {
    flex: 1, background: '#F9FAFB',
    padding: '14px', borderRadius: '10px',
  },
  editLabel: {
    fontSize: '11px', fontWeight: 700, color: '#6B7280',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    marginBottom: '12px',
  },
  field: { marginBottom: '12px' },
  fieldRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  fieldLabel: {
    display: 'block', fontSize: '11px', fontWeight: 700, color: '#6B7280',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px',
  },
  input: {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1.5px solid #E5E7EB', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box', color: plum,
    fontFamily: "'Plus Jakarta Sans', sans-serif", background: '#fff',
  },
  hint: {
    fontSize: '12px', color: '#9CA3AF', marginTop: '6px',
    fontWeight: 500, lineHeight: 1.4,
  },
  error: {
    background: '#FEE2E2', color: '#991B1B',
    padding: '8px 12px', borderRadius: '8px',
    fontSize: '12px', fontWeight: 500, marginTop: '10px', marginBottom: '4px',
  },
  editActions: {
    display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end',
  },
  btnPrimary: {
    padding: '9px 18px', color: '#fff', background: teal,
    border: 'none', borderRadius: '100px',
    fontSize: '12px', fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  btnSecondary: {
    padding: '9px 18px', background: '#fff', color: plum,
    border: '1.5px solid #E5E7EB', borderRadius: '100px',
    fontSize: '12px', fontWeight: 600, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  btnDanger: {
    padding: '9px 18px', background: '#DC2626', color: '#fff',
    border: 'none', borderRadius: '100px',
    fontSize: '12px', fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  deleteBlock: {
    flex: 1, background: '#FEF3C7',
    padding: '12px', borderRadius: '10px',
  },
  deleteText: {
    fontSize: '13px', color: '#92400E', fontWeight: 500, lineHeight: 1.5,
  },
}
